import React, { useRef, useState, useEffect } from 'react';
import {
  IconButton,
  InputAdornment,
  TextField
} from '@mui/material';
import {
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material';

interface SearchBarProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  placeholder?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  searchTerm,
  onSearchChange,
  placeholder = "Search curve names... (Ctrl+F)"
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const onSearchChangeRef = useRef(onSearchChange);
  
  // Internal state for immediate input value (responsive UI)
  const [inputValue, setInputValue] = useState(searchTerm);

  // Keep ref up to date
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Sync external searchTerm changes to internal state
  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);

  // Debounce the onSearchChange calls
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChangeRef.current(inputValue);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [inputValue]); // No onSearchChange dependency to avoid loops

  const handleClearSearch = () => {
    setInputValue('');
    // Clear should be immediate, not debounced
    onSearchChangeRef.current('');
    searchInputRef.current?.focus();
  };

  return (
    <TextField
      size="small"
      placeholder={placeholder}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      fullWidth
      sx={{ mt: 1 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: inputValue ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: 'text.secondary' }}
              aria-label="Clear search"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      inputRef={searchInputRef}
    />
  );
}; 